import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DesignHuddleService } from '@app/shared/services/design-huddle/design-huddle.service';
import { NotificationService } from '@app/core/services/notification/notification.service';
import { LoaderService } from '@app/core/services/loader/loader.service';

/**
 * Design Huddle Editor Component
 * Embeds the Design Huddle editor in an iframe and handles communication
 */
@Component({
  selector: 'app-design-huddle-editor',
  templateUrl: './design-huddle-editor.component.html',
  styleUrls: ['./design-huddle-editor.component.scss']
})
export class DesignHuddleEditorComponent implements OnInit, OnDestroy {
  @ViewChild('editorIframe', { static: false }) editorIframe: ElementRef<HTMLIFrameElement>;

  @Input() projectId: number;
  @Input() editorUrl: string;
  @Input() autoSave: boolean = true;
  @Input() showSaveButton: boolean = true;

  @Output() onSave = new EventEmitter<{ projectId: number }>();
  @Output() onPublish = new EventEmitter<{ projectId: number }>();
  @Output() onClose = new EventEmitter<void>();
  @Output() onError = new EventEmitter<{ error: string }>();

  safeEditorUrl: SafeResourceUrl;
  isEditorLoaded = false;
  isSaving = false;
  private messageListener: (event: MessageEvent) => void;

  constructor(
    private sanitizer: DomSanitizer,
    private designHuddleService: DesignHuddleService,
    private notificationService: NotificationService,
    private loader: LoaderService
  ) { }

  ngOnInit(): void {
    if (!this.editorUrl) {
      this.onError.emit({ error: 'Editor URL is required' });
      return;
    }

    // Sanitize the editor URL for iframe
    this.safeEditorUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.editorUrl);

    // Set up message listener for iframe communication
    this.setupMessageListener();
  }

  ngOnDestroy(): void {
    // Clean up message listener
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
    }
  }

  /**
   * Set up post-message listener for iframe communication
   */
  private setupMessageListener(): void {
    this.messageListener = (event: MessageEvent) => {
      // Verify origin for security (update with actual Design Huddle domain)
      if (!event.origin.includes('designhuddle.com')) {
        return;
      }

      const data = event.data;

      switch (data.type) {
        case 'editor:loaded':
          this.handleEditorLoaded();
          break;

        case 'editor:saved':
          this.handleEditorSaved(data.payload);
          break;

        case 'editor:published':
          this.handleEditorPublished(data.payload);
          break;

        case 'editor:closed':
          this.handleEditorClosed();
          break;

        case 'editor:error':
          this.handleEditorError(data.payload);
          break;

        default:
          console.log('Unknown message from Design Huddle editor:', data);
      }
    };

    window.addEventListener('message', this.messageListener);
  }

  /**
   * Handle editor loaded event
   */
  private handleEditorLoaded(): void {
    this.isEditorLoaded = true;
    this.loader.hide();
    console.log('Design Huddle editor loaded successfully');
  }

  /**
   * Handle editor saved event
   */
  private handleEditorSaved(payload: any): void {
    this.isSaving = false;
    this.notificationService.success('Design saved successfully', 'Success');
    this.onSave.emit({ projectId: this.projectId });
  }

  /**
   * Handle editor published event
   */
  private handleEditorPublished(payload: any): void {
    this.notificationService.success('Design published successfully', 'Success');
    this.onPublish.emit({ projectId: this.projectId });
  }

  /**
   * Handle editor closed event
   */
  private handleEditorClosed(): void {
    this.onClose.emit();
  }

  /**
   * Handle editor error event
   */
  private handleEditorError(payload: any): void {
    const errorMessage = payload?.message || 'An error occurred in the editor';
    this.notificationService.error(errorMessage, 'Error');
    this.onError.emit({ error: errorMessage });
    this.isSaving = false;
  }

  /**
   * Send save command to editor
   */
  saveDesign(): void {
    if (!this.isEditorLoaded || this.isSaving) {
      return;
    }

    this.isSaving = true;
    this.loader.show();

    // Send post message to iframe to trigger save
    this.sendMessageToEditor({
      type: 'command:save',
      payload: {
        projectId: this.projectId
      }
    });
  }

  /**
   * Send publish command to editor
   */
  publishDesign(): void {
    if (!this.isEditorLoaded || this.isSaving) {
      return;
    }

    this.isSaving = true;
    this.loader.show();

    // Send post message to iframe to trigger publish
    this.sendMessageToEditor({
      type: 'command:publish',
      payload: {
        projectId: this.projectId
      }
    });
  }

  /**
   * Close the editor
   */
  closeEditor(): void {
    this.onClose.emit();
  }

  /**
   * Send message to editor iframe
   */
  private sendMessageToEditor(message: any): void {
    if (this.editorIframe?.nativeElement?.contentWindow) {
      // Update with actual Design Huddle origin
      this.editorIframe.nativeElement.contentWindow.postMessage(
        message,
        '*' // In production, replace with specific origin: 'https://editor.designhuddle.com'
      );
    }
  }

  /**
   * Handle iframe load event
   */
  onIframeLoad(): void {
    console.log('Iframe loaded');
    // Editor should send 'editor:loaded' message when ready
  }

  /**
   * Trigger export workflow
   */
  exportDesign(): void {
    if (!this.projectId) {
      this.notificationService.error('Project ID is required for export', 'Error');
      return;
    }

    this.loader.show();

    this.designHuddleService.exportProject({
      projectId: this.projectId,
      format: 'png',
      dpi: 300
    }).subscribe({
      next: (response) => {
        if (response.StatusCode === 200 && response.Data?.ResponseData) {
          const exportJobId = response.Data.ResponseData.exportJobId;

          // Poll for export completion
          this.designHuddleService.pollExportUntilComplete(exportJobId).subscribe({
            next: (status) => {
              this.loader.hide();

              if (status.status === 'completed') {
                this.notificationService.success('Design exported successfully', 'Success');
                // Files are now in Azure Blob Storage via backend
              } else {
                this.notificationService.error(
                  status.errorMessage || 'Export failed',
                  'Error'
                );
              }
            },
            error: (error) => {
              this.loader.hide();
              this.notificationService.error('Export polling failed', 'Error');
              console.error('Export polling error:', error);
            }
          });
        } else {
          this.loader.hide();
          this.notificationService.error('Failed to start export', 'Error');
        }
      },
      error: (error) => {
        this.loader.hide();
        this.notificationService.error('Failed to export design', 'Error');
        console.error('Export error:', error);
      }
    });
  }
}
